import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import callApi from '../helpers/apiCaller';
import config from '../common/config';
import Cookies from 'universal-cookie';

// Cache duration: 1 day in milliseconds
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Secret key for SHA-256 hashing (in production, this should be from env variable)
const CACHE_SECRET = 'lms360_cache_integrity_key';

/**
 * Generate SHA-256 hash for data integrity validation
 * @param {string} data - Data to hash
 * @returns {Promise<string>} SHA-256 hash
 */
const generateHash = async (data) => {
  try {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data + CACHE_SECRET);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return hashHex;
  } catch (error) {
    console.error('Error generating hash:', error);
    throw error;
  }
};

/**
 * Verify SHA-256 hash for data integrity
 * @param {string} data - Data to verify
 * @param {string} hash - Expected hash
 * @returns {Promise<boolean>} True if hash matches
 */
const verifyHash = async (data, hash) => {
  try {
    const generatedHash = await generateHash(data);
    return generatedHash === hash;
  } catch (error) {
    console.error('Error verifying hash:', error);
    return false;
  }
};

/**
 * Get cached user data if still valid (less than 1 day old) and integrity check passes
 * @returns {Promise<{ user: User, accessToken: string } | null>} Cached data or null if expired/invalid/not found
 */
const getCachedUserData = async () => {
  try {
    const cacheTimestamp = window.localStorage.getItem('user_cache_timestamp');
    const cachedUserStr = window.localStorage.getItem('cached_user_data');
    const cachedToken = window.localStorage.getItem('cached_access_token');
    const cachedHash = window.localStorage.getItem('cached_data_hash');

    // Check if all required cache items exist
    if (!cacheTimestamp || !cachedUserStr || !cachedToken || !cachedHash) {
      console.warn('Cache incomplete - missing required items');
      clearUserCache();
      return null;
    }

    // Validate timestamp format
    const timestamp = parseInt(cacheTimestamp, 10);
    if (isNaN(timestamp) || timestamp <= 0) {
      console.warn('Invalid cache timestamp format');
      clearUserCache();
      return null;
    }

    const now = Date.now();

    // Check if cache has expired (older than 1 day)
    if (now - timestamp >= CACHE_DURATION) {
      console.log('Cache expired (> 1 day old)');
      clearUserCache();
      return null;
    }

    // Verify data integrity using SHA-256 hash
    const dataToVerify = cachedUserStr + cachedToken + cacheTimestamp;
    const isValid = await verifyHash(dataToVerify, cachedHash);

    if (!isValid) {
      console.error(
        'Cache integrity check failed - data may be corrupted or tampered'
      );
      clearUserCache();
      return null;
    }

    // Parse user data
    let user;
    try {
      user = JSON.parse(cachedUserStr);

      // Basic validation of user object
      if (!user || typeof user !== 'object' || !user.id || !user.username) {
        console.warn('Invalid user data structure in cache');
        clearUserCache();
        return null;
      }
    } catch (parseError) {
      console.error('Error parsing cached user data:', parseError);
      clearUserCache();
      return null;
    }

    // Validate token format (basic check)
    if (typeof cachedToken !== 'string' || cachedToken.length < 10) {
      console.warn('Invalid token format in cache');
      clearUserCache();
      return null;
    }

    return { user, accessToken: cachedToken };
  } catch (error) {
    console.error('Unexpected error reading user cache:', error);
    clearUserCache();
    return null;
  }
};

/**
 * Save user data to cache with current timestamp and integrity hash
 * @param {User} user - User data to cache
 * @param {string} accessToken - Access token to cache
 * @returns {Promise<void>}
 */
const setCachedUserData = async (user, accessToken) => {
  try {
    // Validate inputs
    if (!user || typeof user !== 'object') {
      console.error('Invalid user data - cannot cache');
      return;
    }

    if (!accessToken || typeof accessToken !== 'string') {
      console.error('Invalid access token - cannot cache');
      return;
    }

    const timestamp = Date.now().toString();
    const userStr = JSON.stringify(user);

    // Generate integrity hash
    const dataToHash = userStr + accessToken + timestamp;
    const hash = await generateHash(dataToHash);

    // Save all cache data atomically
    try {
      window.localStorage.setItem('user_cache_timestamp', timestamp);
      window.localStorage.setItem('cached_user_data', userStr);
      window.localStorage.setItem('cached_access_token', accessToken);
      window.localStorage.setItem('cached_data_hash', hash);

      console.log('✓ User data cached successfully with integrity hash');
    } catch (storageError) {
      console.error('Error writing to localStorage:', storageError);
      // If any storage operation fails, clear all cache to prevent partial state
      clearUserCache();
      throw storageError;
    }
  } catch (error) {
    console.error('Error saving user cache:', error);
    // Clear cache on any error to prevent corrupted state
    clearUserCache();
  }
};

/**
 * Clear user cache from localStorage
 */
const clearUserCache = () => {
  try {
    window.localStorage.removeItem('user_cache_timestamp');
    window.localStorage.removeItem('cached_user_data');
    window.localStorage.removeItem('cached_access_token');
    window.localStorage.removeItem('cached_data_hash');
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * @typedef {Object} User
 * @property {number} id - User ID
 * @property {string} username - Username
 * @property {string} fullname - Full name
 * @property {string} email - Email address
 * @property {Object} roles - User roles (admin, teacher, student, school)
 * @property {number} update_progress - Password update progress flag
 * @property {string} user_logintype_code - Login type code
 */

/**
 * @typedef {Object} LoginCredentials
 * @property {string} username - Username or email
 * @property {string} password - Password
 */

/**
 * @typedef {Object} LoginResponse
 * @property {User} user - User information
 * @property {string} access_token - JWT access token
 */

/**
 * @typedef {Object} UserInfoResponse
 * @property {User} user - User information
 * @property {string|Array} access_token - JWT access token (string for SSO login, empty array for normal login)
 */

/**
 * @typedef {Object} AuthState
 * @property {boolean} isAuthenticated - Whether user is logged in
 * @property {boolean} isLoading - Loading state for auth operations
 * @property {User|null} user - Current user data
 * @property {string|null} accessToken - JWT access token
 * @property {string|null} error - Error message if auth fails
 * @property {boolean} isInitialized - Whether auth state has been initialized
 */

/** @type {AuthState} */
const initialState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  accessToken: null,
  error: null,
  isInitialized: false
};

/**
 * Check if user is currently authenticated by verifying token and fetching user info.
 * - First checks cache: if valid cached data exists (< 1 day old), returns it immediately
 * - Otherwise checks for existing access_token in cookies/localStorage
 * - If no token found, still tries API call (for SSO session detection)
 * - For SSO login, the API response will include a new access_token (string) that should be stored
 * - For normal login, access_token will be empty array, so existing token is preserved
 * - Updates stored tokens and user data based on API response
 * - Saves user data to cache with timestamp for future use
 * - 401 required_change_password_exception is handled by apiCaller.js
 * @returns {Promise<{user: User, accessToken: string}>} User data and access token if authenticated
 */
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      // Check cache first - if valid cached data exists, use it
      const cachedData = await getCachedUserData();
      if (cachedData) {
        return cachedData;
      }

      const cookies = new Cookies();
      let accessToken =
        cookies.get('access_token') || cookies.get('AccessToken');
      if (!accessToken) {
        accessToken = window.localStorage.getItem('AccessToken');
      }

      // For SSO, we might not have a token initially but the server might still recognize the session
      // So we try to call the API even without a token to check for SSO authentication
      const header = accessToken ? { authorization: accessToken } : {};
      const url = config.getRootURL() + '/gd/v3/user_infos';

      const response = await callApi(url, 'GET', {}, header);

      // If no token and API call fails, user is not authenticated
      if (!accessToken && !response) {
        return rejectWithValue('No access token found');
      }

      if (!response || !response.data?.data?.user) {
        return rejectWithValue('Invalid response from server');
      }

      const { user, access_token } = response.data.data;

      // For SSO login, the response includes a new access_token that should be stored
      // For normal login, access_token will be empty array, so we keep the existing token
      let finalAccessToken = accessToken; // Default to current token
      if (
        access_token &&
        typeof access_token === 'string' &&
        access_token.length > 0
      ) {
        finalAccessToken = access_token;
        // Update stored access token with the new one from API response (SSO case)
        cookies.set('AccessToken', access_token, { path: '/' });
        window.localStorage.setItem('AccessToken', access_token);
      } else if (!accessToken) {
        // If we had no token initially and API didn't provide a valid one, user is not authenticated
        return rejectWithValue('No access token available');
      }

      // Update user data in cookies and cache
      cookies.set('user_store', JSON.stringify(user).replace(/</g, '\\u003c'), {
        path: '/'
      });
      config.AddDataIntoCache('user_store', '/', JSON.stringify(user));
      config.AddDataIntoCache('is_logged', '/', true);

      // Save to cache with timestamp and integrity hash for future use (valid for 1 day)
      try {
        await setCachedUserData(user, finalAccessToken);
      } catch (cacheError) {
        // Non-critical error - log but don't fail auth
        console.warn('Failed to save cache, but auth succeeded:', cacheError);
      }

      return { user, accessToken: finalAccessToken };
    } catch (error) {
      console.error('Authentication check failed:', error);
      return rejectWithValue(error.message || 'Authentication check failed');
    }
  }
);

/**
 * Login user with username and password
 * @param {LoginCredentials} credentials - Login credentials
 * @returns {Promise<{user: User, accessToken: string}>} Login response
 */
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const apiBaseUrl = config.getRootURL() + '/gd/v3/login';
      const body = {
        username: credentials.username,
        password: credentials.password
      };

      const response = await callApi(apiBaseUrl, 'POST', body);

      if (!response || !response.data?.data?.user) {
        return rejectWithValue('Invalid login response');
      }

      const { user, access_token } = response.data.data;
      const cookies = new Cookies();

      // Store user data and token
      cookies.set('user_store', JSON.stringify(user).replace(/</g, '\\u003c'), {
        path: '/'
      });
      cookies.set('AccessToken', access_token, { path: '/' });
      window.localStorage.setItem('AccessToken', access_token);

      config.AddDataIntoCache('user_store', '/', JSON.stringify(user));
      config.AddDataIntoCache('is_logged', '/', true);

      // Save to cache with timestamp and integrity hash for future use (valid for 1 day)
      try {
        await setCachedUserData(user, access_token);
      } catch (cacheError) {
        // Non-critical error - log but don't fail login
        console.warn('Failed to save cache, but login succeeded:', cacheError);
      }

      return { user, accessToken: access_token };
    } catch (error) {
      console.error('Login failed:', error);
      return rejectWithValue(
        error.response?.data?.errors?.[0]?.message ||
          error.message ||
          'Login failed'
      );
    }
  }
);

/**
 * Logout user and clear all stored data including cache
 */
export const logout = createAsyncThunk('auth/logout', async () => {
  const cookies = new Cookies();

  // Clear localStorage
  window.localStorage.removeItem('user_store');
  window.localStorage.removeItem('phone_number');
  window.localStorage.removeItem('course_id');
  window.localStorage.removeItem('register_info');
  window.localStorage.removeItem('AccessToken');

  // Clear cache
  config.DeleteDataIntoCache('user_store', '/');
  config.DeleteDataIntoCache('is_logged', '/');

  // Clear user data cache
  clearUserCache();

  // Clear cookies
  cookies.remove('user_store');
  cookies.remove('AccessToken');
  cookies.remove('access_token');
  cookies.remove('OTPStore');
  cookies.remove('showblur');

  return null;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Clear error message
     * @param {AuthState} state
     */
    clearError(state) {
      state.error = null;
    },
    /**
     * Set loading state
     * @param {AuthState} state
     * @param {{ payload: boolean }} action
     */
    setLoading(state, action) {
      state.isLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.isInitialized = true;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.error = action.payload;
        state.isInitialized = true;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.isInitialized = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.error = null;
        state.isLoading = false;
        state.isInitialized = true;
      });
  }
});

export const { clearError, setLoading } = authSlice.actions;

// Selectors
/**
 * Get authentication state
 * @param {Object} state - Redux root state
 * @returns {boolean} Is user authenticated
 */
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;

/**
 * Get current user
 * @param {Object} state - Redux root state
 * @returns {User|null} Current user data
 */
export const selectUser = (state) => state.auth.user;

/**
 * Get access token
 * @param {Object} state - Redux root state
 * @returns {string|null} Current access token
 */
export const selectAccessToken = (state) => state.auth.accessToken;

/**
 * Get auth loading state
 * @param {Object} state - Redux root state
 * @returns {boolean} Loading state
 */
export const selectAuthLoading = (state) => state.auth.isLoading;

/**
 * Get auth error
 * @param {Object} state - Redux root state
 * @returns {string|null} Error message
 */
export const selectAuthError = (state) => state.auth.error;

/**
 * Get auth initialization state
 * @param {Object} state - Redux root state
 * @returns {boolean} Is auth initialized
 */
export const selectAuthInitialized = (state) => state.auth.isInitialized;

/**
 * Get user role
 * @param {Object} state - Redux root state
 * @returns {Object|null} User roles
 */
export const selectUserRoles = (state) => state.auth.user?.roles || null;

/**
 * Check if user has specific role
 * @param {Object} state - Redux root state
 * @param {string} role - Role to check (admin, teacher, student, school)
 * @returns {boolean} Whether user has the role
 */
export const selectHasRole = (state, role) => {
  const roles = selectUserRoles(state);
  return roles ? !!roles[role] : false;
};

/**
 * Get user's full name
 * @param {Object} state - Redux root state
 * @returns {string} User's full name or empty string
 */
export const selectUserFullName = (state) => state.auth.user?.fullname || '';

/**
 * Get user's username
 * @param {Object} state - Redux root state
 * @returns {string} Username or empty string
 */
export const selectUsername = (state) => state.auth.user?.username || '';

export default authSlice.reducer;
